import React, { useCallback, useEffect, useMemo, useState } from "react";
import s from "./material-composer.module.scss";
import TextareaAutosize from "react-textarea-autosize";
import {
  IconArrowDown,
  IconFile,
  IconPaperclip,
  IconPencil,
  IconPlus,
  IconX,
} from "@tabler/icons-react";
import classNames from "classnames";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import { useAttachFileModal } from "@features/attach-files";
import { VoiceRecorder } from "@shared/ui/voice-recorder/voice-recorder";
import { blobToFile } from "@utils/lib/blobToFile";
import { isImage } from "@utils/lib/isImage";
import { ImageEditorModal } from "@features/appointment/image-editor";

const isImageFile = (file) => {
  if (!file) return false;
  if (file?.type) return file.type.startsWith("image/");
  return isImage(file);
};

export const MaterialComposer = ({
  addButtonLabel = "Добавить материал",
  submitLabel = "Отправить",
  cancelLabel = "Отменить",
  placeholder = "Введите текст...",
  initialText = "",
  initialAttachments,
  startExpanded = false,
  showAudio = true,
  showAttachmentButton = true,
  showCollapseButton = false,
  onSubmit = async () => {},
  onCancel,
}) => {
  const normalizedInitialAttachments = useMemo(
    () => initialAttachments || [],
    [initialAttachments]
  );

  const [text, setText] = useState(initialText);
  const [myFiles, setMyFiles] = useState(normalizedInitialAttachments);
  const [blob, setBlob] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFormVisible, setIsFormVisible] = useState(startExpanded);
  const [editingImage, setEditingImage] = useState(null); // { index, fileName, src, shouldRevoke }
  const { open: openAttachFileModal } = useAttachFileModal();

  const maxFiles = 5;

  const appendFiles = useCallback((filesToAppend) => {
    if (!filesToAppend?.length) return;

    setMyFiles((prev) => [...prev, ...filesToAppend].slice(0, maxFiles));
  }, []);

  useEffect(() => {
    setText(initialText);
  }, [initialText]);

  useEffect(() => {
    setMyFiles(normalizedInitialAttachments);
  }, [normalizedInitialAttachments]);

  useEffect(() => {
    setIsFormVisible(startExpanded);
  }, [startExpanded]);

  const onDrop = useCallback((acceptedFiles) => {
    appendFiles(acceptedFiles);
  }, [appendFiles]);

  const { getInputProps, getRootProps, isDragActive } = useDropzone({
    maxFiles,
    onDrop,
    maxSize: 104857600,
    noClick: true,
    noKeyboard: true,
    disabled: !showAttachmentButton || isLoading,
  });

  const clearAndClose = useCallback(() => {
    setText(initialText);
    setMyFiles(normalizedInitialAttachments);
    setBlob(null);
    setIsLoading(false);
    setIsFormVisible(false);
  }, [initialText, normalizedInitialAttachments]);

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    clearAndClose();
  };

  const removeFile = (index) => {
    setMyFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index));
  };

  const openImageEditor = useCallback(async ({ file, index, preview, isObjectUrl }) => {
    let src = preview;
    let shouldRevoke = false;

    // Картинки с хранилища грузим как blob — иначе canvas не сможет её прочитать (CORS)
    if (!isObjectUrl) {
      try {
        const response = await fetch(preview);
        const imageBlob = await response.blob();
        src = URL.createObjectURL(imageBlob);
        shouldRevoke = true;
      } catch {
        toast.error("Не удалось загрузить изображение для редактирования");
        return;
      }
    }

    setEditingImage({ index, fileName: file.name, src, shouldRevoke });
  }, []);

  const closeImageEditor = useCallback(() => {
    setEditingImage((prev) => {
      if (prev?.shouldRevoke) URL.revokeObjectURL(prev.src);
      return null;
    });
  }, []);

  const handleImageEdited = useCallback(
    (editedFile) => {
      setMyFiles((prev) =>
        prev.map((file, fileIndex) =>
          fileIndex === editingImage?.index ? editedFile : file
        )
      );
      closeImageEditor();
    },
    [editingImage, closeImageEditor]
  );

  const imageFiles = useMemo(
    () => myFiles.map((file, index) => ({ file, index })).filter(({ file }) => isImageFile(file)),
    [myFiles]
  );

  const imagePreviews = useMemo(
    () =>
      imageFiles.map(({ file, index }) => {
        if (file instanceof File) {
          return {
            file,
            index,
            preview: URL.createObjectURL(file),
            isObjectUrl: true,
          };
        }

        return {
          file,
          index,
          preview: `${process.env.REACT_APP_STORAGE_URL}${file.file}`,
          isObjectUrl: false,
        };
      }),
    [imageFiles]
  );

  useEffect(() => {
    return () => {
      imagePreviews.forEach(({ preview, isObjectUrl }) => {
        if (isObjectUrl) URL.revokeObjectURL(preview);
      });
    };
  }, [imagePreviews]);

  const nonImageFiles = useMemo(
    () => myFiles.map((file, index) => ({ file, index })).filter(({ file }) => !isImageFile(file)),
    [myFiles]
  );

  const existingFiles = useMemo(
    () => myFiles.filter((file) => !(file instanceof File)),
    [myFiles]
  );
  const newFiles = useMemo(
    () => myFiles.filter((file) => file instanceof File),
    [myFiles]
  );

  const canSubmit = Boolean(text.trim() || myFiles.length || blob);
  const shouldShowNonImageFiles = nonImageFiles.length > 0;
  const shouldShowBlobAsFile = Boolean(blob) && !showAudio;

  const handleSubmit = async () => {
    if (!canSubmit || isLoading) return;

    setIsLoading(true);

    try {
      const attachments = blob ? [...newFiles, blob] : newFiles;
      await onSubmit({
        text: text.trim(),
        attachments,
        existingFileIds: existingFiles.map((file) => file?.id).filter(Boolean),
      });
      if (onCancel) {
        onCancel();
      } else {
        clearAndClose();
      }
    } catch {
      setIsLoading(false);
    }
  };

  const handlePaste = useCallback(
    (event) => {
      if (!showAttachmentButton || isLoading) return;

      const pastedFiles = Array.from(event.clipboardData?.files || []);

      if (!pastedFiles.length) {
        return;
      }

      appendFiles(pastedFiles);
    },
    [appendFiles, isLoading, showAttachmentButton]
  );

  if (!isFormVisible) {
    return (
      <div className={s.card}>
        <button className={s.addButton} onClick={() => setIsFormVisible(true)}>
          <IconPlus size={18} />
          {addButtonLabel}
        </button>
      </div>
    );
  }

  return (
    <div
      {...getRootProps()}
      className={classNames(
        s.card,
        isLoading ? s.isLoading : "",
        isDragActive ? s.isDragActive : ""
      )}
      onPaste={handlePaste}
    >
      <input {...getInputProps()} />

      {showAttachmentButton && isDragActive ? (
        <div className={s.dropzoneOverlay}>
          <div className={s.dropzoneOverlayInner}>
            <IconPaperclip size={24} />
            <span>Отпустите файл, чтобы прикрепить его</span>
          </div>
        </div>
      ) : null}

      <div className={s.topRow}>
        <div className={s.images}>
          {imagePreviews.map(({ file, index, preview, isObjectUrl }) => (
            <div className={s.imageItem} key={`${file.name}-${index}`}>
              <img src={preview} alt={file.name} />
              <button
                className={s.editImage}
                onClick={() => openImageEditor({ file, index, preview, isObjectUrl })}
                title="Редактировать изображение"
              >
                <IconPencil size={14} />
              </button>
              <button className={s.removeImage} onClick={() => removeFile(index)}>
                <IconX size={14} />
              </button>
            </div>
          ))}
        </div>
        {showCollapseButton ? (
          <button className={s.collapseButton} onClick={handleCancel}>
            <IconArrowDown size={22} />
          </button>
        ) : null}
      </div>

      {(shouldShowNonImageFiles || shouldShowBlobAsFile) && (
        <div className={s.fileList}>
          {shouldShowNonImageFiles &&
            nonImageFiles.map(({ file, index }) => (
            <div className={s.fileRow} key={`${file.name}-${index}`}>
              <div className={s.fileInfo}>
                <div className={s.fileIcon}>
                  <IconFile size={18} />
                </div>
                <span>{file.name}</span>
              </div>
              <button className={s.removeFile} onClick={() => removeFile(index)}>
                <IconX size={16} />
              </button>
            </div>
          ))}
          {shouldShowBlobAsFile && (
            <div className={s.fileRow}>
              <div className={s.fileInfo}>
                <div className={s.fileIcon}>
                  <IconFile size={18} />
                </div>
                <span>{blob.name}</span>
              </div>
              <button className={s.removeFile} onClick={() => setBlob(null)}>
                <IconX size={16} />
              </button>
            </div>
          )}
        </div>
      )}

      <div className={s.textareaWrapper}>
        <TextareaAutosize
          onChange={(event) => setText(event.target.value)}
          value={text}
          minRows={6}
          maxRows={10}
          placeholder={placeholder}
        />
      </div>

      <div className={s.footer}>
        {showAudio || showAttachmentButton ? (
          <div className={classNames(s.leftActions)}>
            {showAudio ? (
              <div className={classNames(s.micAction, blob ? s.fullWidth : "")}>
                <VoiceRecorder
                  blob={blob}
                  onRecordingComplete={(res) => {
                    if (!res) {
                      setBlob(null);
                      return;
                    }
                    setBlob(blobToFile(res, `${Date.now()}.mp3`));
                  }}
                />
              </div>
            ) : null}
            {showAttachmentButton ? (
              <button
                className={s.iconAction}
                onClick={() =>
                  openAttachFileModal({
                    multiple: true,
                    maxFiles: maxFiles - myFiles.length,
                    onSelect: appendFiles,
                  })
                }
              >
                <IconPaperclip size={20} />
              </button>
            ) : null}
          </div>
        ) : null}

        <div className={s.rightActions}>
          <button className={s.cancelButton} onClick={handleCancel} disabled={isLoading}>
            {cancelLabel}
          </button>
          <button className={s.submitButton} onClick={handleSubmit} disabled={!canSubmit || isLoading}>
            {submitLabel}
          </button>
        </div>
      </div>

      <ImageEditorModal
        isOpen={Boolean(editingImage)}
        close={closeImageEditor}
        src={editingImage?.src}
        fileName={editingImage?.fileName}
        onSave={handleImageEdited}
      />
    </div>
  );
};
